import type { ReactNode } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import useImageVariant from "@/hooks/useImageVariant";
import {
  APPBAR_HEIGHT,
  CONTENT_GUTTER,
  PAGE_PADDING,
  SLATE_APPBAR_HEIGHT,
  gutterFor,
} from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import type { DesignFlags } from "@/hooks/useDesignType";

/**
 * The category tab strip — `nt` (`TabsSection`) → `nn` (the sticky wrapper) →
 * `t5` (`CategoryTabsHeader`) → one of FOUR tab-item renderers.
 *
 * The renderer is chosen by `settings.web_design_type` alone; the per-category
 * `display_type` has nothing to do with it. The selection ladder is verbatim:
 *
 *   isModernVibrantDesign ? t2 → t$   (fixed 200px text cards + corner wedge)
 *   : isVibrantDesign     ? tL → tO   (vertical icon cards, uppercase label)
 *   : isModernSlateDesign ? tJ → tV   (horizontal pill, circular avatar)
 *   :                       tC        (text pills with a slide-in underline)
 *
 * ⚠ `MODERN_SLATE_VIBRANT` lands on the VIBRANT branch, because
 * `isModernSlateDesign` is `endsWith("SLATE")` while the navbar's
 * `isSlateDesign` is `includes("SLATE")`. That is the shipped behaviour.
 *
 * ⚠ The strip is the documented EXCEPTION to the modern gutter: `t4` and `ne`
 * use the non-modern 7% values at `xxl`/`xxxl` unconditionally, even when the
 * page around them is at 12%.
 */

export interface CategoryTabOption {
  _id: string;
  /** Already formatted as `${category.name} (${items.length})` by the caller. */
  name: string;
  image_url?: string | null;
  icon?: ReactNode;
}

/* ------------------------------ chrome — t4/t3/t6/t8 ----------------------- */

/** `t4` — the strip frame. */
const Frame = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isModernDesign" &&
    prop !== "isVibrantDesign" &&
    prop !== "isModernSlateDesign",
})<{
  isModernDesign?: boolean;
  isVibrantDesign?: boolean;
  isModernSlateDesign?: boolean;
}>(({ theme, isModernDesign, isVibrantDesign, isModernSlateDesign }) => ({
  position: "relative",
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  ...(isModernDesign
    ? {
        ...(isVibrantDesign
          ? {}
          : { borderBottom: `1px solid ${theme.palette.divider}` }),
      }
    : {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: PAGE_PADDING,
        marginLeft: PAGE_PADDING,
        marginRight: PAGE_PADDING,
        // The non-modern gutters, deliberately, for both design families.
        [theme.breakpoints.up("lg")]: {
          marginLeft: CONTENT_GUTTER.lg,
          marginRight: CONTENT_GUTTER.lg,
        },
        [theme.breakpoints.up("xl")]: {
          marginLeft: CONTENT_GUTTER.xl,
          marginRight: CONTENT_GUTTER.xl,
        },
        [theme.breakpoints.up("xxl")]: {
          marginLeft: CONTENT_GUTTER.xxl,
          marginRight: CONTENT_GUTTER.xxl,
        },
        [theme.breakpoints.up("xxxl")]: {
          marginLeft: CONTENT_GUTTER.xxxl,
          marginRight: CONTENT_GUTTER.xxxl,
        },
      }),
  ...(isVibrantDesign
    ? {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        border: "none",
        borderRadius: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }
    : isModernSlateDesign
      ? {
          border: "none",
          borderRadius: "none",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }
      : { backgroundColor: theme.palette.background.default }),
}));

/** `t3` — the horizontal scroll port. */
const ScrollPort = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isModernSlateDesign",
})<{ isModernSlateDesign?: boolean }>(({ theme, isModernSlateDesign }) => ({
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  scrollBehavior: "smooth",
  scrollbarWidth: "none",
  ...(isModernSlateDesign
    ? { padding: theme.spacing(1.5) }
    : {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
      }),
  "&::-webkit-scrollbar": { display: "none" },
}));

/** `t6` — the flex row the tab items sit in. */
const TabRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isVibrantDesign",
})<{ isVibrantDesign?: boolean }>(({ theme, isVibrantDesign }) =>
  isVibrantDesign
    ? {
        display: "flex",
        gap: theme.spacing(1),
        width: "fit-content",
        padding: theme.spacing(0.5),
        [theme.breakpoints.up("md")]: { gap: theme.spacing(1.5) },
      }
    : { display: "flex", gap: "8px", width: "fit-content" },
);

/** `t8` — the ±200px scroll arrows. Hidden below `md`. */
const ScrollArrow = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "direction",
})<{ direction: "left" | "right" }>(({ theme, direction }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: Z_INDEX.MID,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
  border: `1px solid ${theme.palette.divider}`,
  width: "32px",
  height: "32px",
  display: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    boxShadow: `0 6px 20px ${theme.palette.primary.main}40`,
    transform: "translateY(-50%) scale(1.05)",
  },
  ...(direction === "left" && { left: "8px" }),
  ...(direction === "right" && { right: "8px" }),
  [theme.breakpoints.up("md")]: {
    display: "flex",
    width: "36px",
    height: "36px",
  },
}));

/* ------------------- variant 1 — default text pills (tC) ------------------- */

/** `tR` */
const TextTabButton = styled("button")({
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  outline: "none",
  "&:focus": { outline: "none" },
});

/**
 * `tI`, restyled to the reference's PILL.
 *
 * The original's default tab is a soft-cornered text label with a 5px underline
 * that slides in on selection (`tT`) and a 24px stub on hover (`tM`). The
 * reference site uses fully rounded pills instead, with the selected one FILLED
 * in the brand colour — a much stronger "you are here" on a strip that scrolls
 * horizontally and can leave the selected tab half off-screen.
 *
 * Both indicators are gone rather than left unused: an underline under a
 * fully-rounded filled pill reads as a rendering fault.
 *
 * The colour is `palette.primary` throughout — this tenant's own `brand_color`,
 * as it always was. Nothing here is a fixed hex.
 *
 * `minHeight: 40` is the touch-target floor; the strip is the primary
 * navigation on a phone.
 */
const TextTabLabel = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  minHeight: 40,
  borderRadius: 999,
  fontSize: "0.9rem",
  fontWeight: isActive ? 700 : 500,
  color: isActive
    ? theme.palette.primary.contrastText
    : theme.palette.text.secondary,
  backgroundColor: isActive
    ? theme.palette.primary.main
    : theme.palette.background.default,
  border: `1px solid ${
    isActive ? theme.palette.primary.main : alpha(theme.palette.divider, 0.8)
  }`,
  boxShadow: isActive ? `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}` : "none",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.08),
    borderColor: isActive
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.4),
    color: isActive
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
  },
  [theme.breakpoints.up("sm")]: {
    padding: `${theme.spacing(1.15)} ${theme.spacing(2.25)}`,
    fontSize: "0.93rem",
  },
  [theme.breakpoints.up("md")]: {
    padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
    fontSize: "0.95rem",
  },
}));

/* --------------------- variant 2 — vibrant cards (tO) ---------------------- */

/** `tD` */
const VibrantTabButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(0.75),
  padding: theme.spacing(1),
  width: "100px",
  minWidth: "100px",
  background: isActive
    ? theme.palette.primary.main
    : theme.palette.background.default,
  border: "none",
  borderRadius: "12px",
  cursor: "pointer",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  boxShadow: isActive
    ? `0 4px 12px ${theme.palette.primary.main}40`
    : "0 1px 4px rgba(0, 0, 0, 0.08)",
  outline: "none",
  "&:hover": {
    boxShadow: isActive
      ? `0 6px 16px ${theme.palette.primary.main}50`
      : "0 4px 12px rgba(0, 0, 0, 0.12)",
    background: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
  "&:focus": { outline: "none" },
  "&:active": { transform: "scale(0.98)" },
  [theme.breakpoints.up("sm")]: {
    width: "110px",
    minWidth: "110px",
    padding: theme.spacing(1.25),
    borderRadius: "14px",
    gap: theme.spacing(1),
  },
  [theme.breakpoints.up("md")]: {
    width: "130px",
    minWidth: "130px",
    padding: theme.spacing(1.5),
    borderRadius: "16px",
  },
}));

/** `tz` — the 40/50/60px rounded icon tile. */
const VibrantIconFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  width: "40px",
  height: "40px",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: isActive ? "rgba(255, 255, 255, 0.95)" : theme.palette.grey[50],
  flexShrink: 0,
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.up("sm")]: {
    width: "50px",
    height: "50px",
    borderRadius: "8px",
  },
  [theme.breakpoints.up("md")]: {
    width: "60px",
    height: "60px",
    borderRadius: "10px",
  },
}));

/** `tA` */
const TabImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "button:hover &": { transform: "scale(1.05)" },
});

/** `tN` — the initial-letter fallback when a category has no image. */
const InitialFallback = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.25rem",
  fontWeight: 600,
  background: isActive
    ? `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`
    : `linear-gradient(135deg, ${theme.palette.grey[200]}, ${theme.palette.grey[300]})`,
  color: isActive ? "#fff" : theme.palette.grey[600],
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  textTransform: "uppercase",
  [theme.breakpoints.up("md")]: { fontSize: "1.5rem" },
}));

/** `tF` — the icon slot (used by the synthetic tabs, which carry an `icon`). */
const IconSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  background: isActive
    ? theme.palette.grey[600]
    : `linear-gradient(135deg, ${theme.palette.grey[200]}, ${theme.palette.grey[300]})`,
  color: isActive ? "#fff" : theme.palette.grey[600],
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: "inherit",
  "& svg": {
    fontSize: "1.25rem",
    [theme.breakpoints.up("md")]: { fontSize: "1.5rem" },
  },
}));

/** `tW` */
const VibrantLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  fontSize: "0.7rem",
  fontWeight: isActive ? 600 : 500,
  color: isActive ? "#fff" : theme.palette.text.primary,
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  lineHeight: 1.3,
  maxWidth: "100%",
  minHeight: "1.8em",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  wordBreak: "break-word",
}));

/* -------------------- variant 3 — modern-slate pills (tV) ------------------ */

/** `tK` */
const SlateTabButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(1.1),
  padding: theme.spacing(1.1),
  width: "140px",
  minWidth: "140px",
  background: isActive
    ? theme.palette.primary.main
    : theme.palette.mode === "dark"
      ? theme.palette.grey[900]
      : theme.palette.background.default,
  border: "none",
  borderRadius: "40px",
  cursor: "pointer",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  boxShadow: isActive
    ? `0 4px 12px ${theme.palette.primary.main}40`
    : theme.palette.mode === "dark"
      ? "0 3px 8px rgba(255, 255, 255, 0.08)"
      : "0 3px 8px rgba(0, 0, 0, 0.08)",
  outline: "none",
  "&:hover": {
    boxShadow: isActive
      ? `0 6px 16px ${theme.palette.primary.main}50`
      : "0 4px 12px rgba(0, 0, 0, 0.2)",
    background: isActive
      ? theme.palette.primary.main
      : theme.palette.action.hover,
  },
  "&:focus": { outline: "none" },
  "&:active": { transform: "scale(0.98)" },
  [theme.breakpoints.up("sm")]: {
    width: "160px",
    minWidth: "160px",
    padding: theme.spacing(1.25),
    gap: theme.spacing(1.2),
  },
  // Verbatim: the width grows to 200 while the MIN width DROPS to 130.
  [theme.breakpoints.up("md")]: {
    width: "200px",
    minWidth: "130px",
    padding: theme.spacing(1.5),
  },
}));

/** `tG` — circular, unlike the Vibrant tile's rounded square. */
const SlateAvatarFrame = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  backgroundColor: isActive ? "rgba(255, 255, 255, 0.95)" : theme.palette.grey[50],
  flexShrink: 0,
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.up("md")]: { width: "42px", height: "42px" },
}));

/** `tH` */
const SlateImage = styled("img")({
  width: "100%",
  height: "100%",
  borderRadius: "inherit",
  objectFit: "cover",
  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "button:hover &": { transform: "scale(1.05)" },
});

/** `tQ` — the Slate icon slot; note it is dark-mode aware where `tF` is not. */
const SlateIconSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  background: isActive
    ? theme.palette.grey[600]
    : theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${theme.palette.grey[500]}, ${theme.palette.grey[400]})`
      : `linear-gradient(135deg, ${theme.palette.grey[200]}, ${theme.palette.grey[300]})`,
  color: isActive
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  borderRadius: "inherit",
  "& svg": {
    fontSize: "1.25rem",
    [theme.breakpoints.up("md")]: { fontSize: "1.5rem" },
  },
}));

/** `tX` — left-aligned, unlike `tW`. */
const SlateLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: "0.7rem",
  fontWeight: isActive ? 600 : 500,
  color: isActive ? "#fff" : theme.palette.text.primary,
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  lineHeight: 1.3,
  maxWidth: "100%",
  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  textTransform: "uppercase",
  letterSpacing: "0.15px",
  wordBreak: "break-word",
}));

/* ------------------ variant 4 — modern-vibrant cards (t$) ------------------ */

/** `t0` */
const ModernVibrantTabButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  position: "relative",
  gap: theme.spacing(0.5),
  padding: theme.spacing(1),
  borderRadius: "12px",
  background: isActive
    ? "transparent"
    : "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
  border: isActive
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  outline: "none",
  boxShadow: isActive
    ? `0 8px 28px ${theme.palette.primary.main}45, 0 4px 14px ${theme.palette.secondary.main}35`
    : "0 2px 12px rgba(0,0,0,0.06)",
  transform: isActive ? "translateY(-2px)" : "translateY(0)",
  width: "200px",
  backgroundColor: theme.palette.background.default,
  "&:hover": {
    background: isActive
      ? "transparent"
      : `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.secondary.main}8 100%)`,
    border: isActive ? `2px solid ${theme.palette.primary.main}` : "none",
    boxShadow: isActive
      ? `0 12px 36px ${theme.palette.primary.main}55, 0 6px 20px ${theme.palette.secondary.main}45`
      : `0 4px 20px ${theme.palette.primary.main}20`,
    transform: isActive ? "translateY(-2px)" : "translateY(-1px)",
  },
  [theme.breakpoints.up("xs")]: {
    padding: theme.spacing(1.25),
    borderRadius: "14px",
  },
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(1.5),
    borderRadius: "16px",
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2),
    borderRadius: "18px",
  },
}));

/** `t1` */
const ModernVibrantLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  fontSize: isActive ? "0.75rem" : "0.7rem",
  fontWeight: isActive ? 700 : 600,
  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
  textAlign: "center",
  whiteSpace: "wrap",
  letterSpacing: "0.5px",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  [theme.breakpoints.up("xs")]: { fontSize: isActive ? "0.8rem" : "0.75rem" },
  [theme.breakpoints.up("sm")]: { fontSize: isActive ? "0.85rem" : "0.8rem" },
  [theme.breakpoints.up("md")]: { fontSize: isActive ? "0.9rem" : "0.85rem" },
}));

/* -------------------------------- tab items -------------------------------- */

interface TabItemProps {
  tab: CategoryTabOption;
  isActive: boolean;
  onClick: () => void;
  innerRef: (node: HTMLButtonElement | null) => void;
}

/** `tO` — `VibrantCategoryTabItem`. Tab art is requested at `64x64`. */
function VibrantTabItem({ tab, isActive, onClick, innerRef }: TabItemProps) {
  const { imageSrc, onImageError } = useImageVariant(tab.image_url ?? undefined, "64x64");
  return (
    <VibrantTabButton ref={innerRef} onClick={onClick} isActive={isActive}>
      <VibrantIconFrame isActive={isActive}>
        {tab.icon ? (
          <IconSlot isActive={isActive}>{tab.icon}</IconSlot>
        ) : tab.image_url ? (
          <TabImage src={imageSrc} alt={tab.name} onError={onImageError} />
        ) : (
          <InitialFallback isActive={isActive}>
            {tab.name.charAt(0).toUpperCase()}
          </InitialFallback>
        )}
      </VibrantIconFrame>
      <VibrantLabel isActive={isActive}>{tab.name}</VibrantLabel>
    </VibrantTabButton>
  );
}

/** `tV` — `ModernSlateCategoryTabItem`. */
function SlateTabItem({ tab, isActive, onClick, innerRef }: TabItemProps) {
  const { imageSrc, onImageError } = useImageVariant(tab.image_url ?? undefined, "64x64");
  return (
    <SlateTabButton ref={innerRef} onClick={onClick} isActive={isActive}>
      <SlateAvatarFrame isActive={isActive}>
        {tab.icon ? (
          <SlateIconSlot isActive={isActive}>{tab.icon}</SlateIconSlot>
        ) : tab.image_url ? (
          <SlateImage src={imageSrc} alt={tab.name} onError={onImageError} />
        ) : (
          <InitialFallback isActive={isActive}>
            {tab.name.charAt(0).toUpperCase()}
          </InitialFallback>
        )}
      </SlateAvatarFrame>
      <SlateLabel isActive={isActive}>{tab.name}</SlateLabel>
    </SlateTabButton>
  );
}

/** `t$` — `ModernVibrantCategoryTabItem`, with its decorative corner wedge. */
function ModernVibrantTabItem({
  tab,
  isActive,
  onClick,
  innerRef,
}: TabItemProps) {
  return (
    <ModernVibrantTabButton
      ref={innerRef}
      onClick={onClick}
      isActive={isActive}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: { xxs: 30, sm: 50, md: 60 },
          height: { xxs: 30, sm: 50, md: 60 },
          background: (theme) =>
            `linear-gradient(135deg, ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.1)"
            } 20%, ${theme.palette.primary.light} 250%)`,
          borderRadius: "0 12px 0 50px",
          zIndex: 0,
        }}
      />
      <ModernVibrantLabel isActive={isActive}>{tab.name}</ModernVibrantLabel>
    </ModernVibrantTabButton>
  );
}

/* ------------------------------ t5 — the header ---------------------------- */

interface CategoryTabsHeaderProps {
  options: CategoryTabOption[];
  activeIndex: number;
  onTabChange: (index: number) => void;
  flags: DesignFlags;
}

function CategoryTabsHeader({
  options,
  activeIndex,
  onTabChange,
  flags,
}: CategoryTabsHeaderProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const node = scrollRef.current;
    const update = () => {
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    };
    update();
    if (!node) return undefined;
    node.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      node.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [options]);

  // The active tab is re-centred in the port on every change, which is what
  // keeps a long strip readable while the page scrolls through categories.
  useEffect(() => {
    const port = scrollRef.current;
    const tab = tabRefs.current[activeIndex];
    if (!port || !tab) return;
    port.scrollTo({
      left: tab.offsetLeft + tab.offsetWidth / 2 - port.clientWidth / 2,
      behavior: "smooth",
    });
  }, [activeIndex]);

  const {
    isModernDesign,
    isVibrantDesign,
    isModernSlateDesign,
    isModernVibrantDesign,
  } = flags;

  const setTabRef = (index: number) => (node: HTMLButtonElement | null) => {
    tabRefs.current[index] = node;
  };

  const items = options.map((option, index) => {
    const shared = {
      tab: option,
      isActive: index === activeIndex,
      onClick: () => onTabChange(index),
      innerRef: setTabRef(index),
    };
    if (isModernVibrantDesign)
      return <ModernVibrantTabItem key={option._id} {...shared} />;
    if (isVibrantDesign) return <VibrantTabItem key={option._id} {...shared} />;
    if (isModernSlateDesign)
      return <SlateTabItem key={option._id} {...shared} />;
    return (
      <TextTabButton
        key={option._id}
        ref={setTabRef(index)}
        onClick={() => onTabChange(index)}
      >
        <TextTabLabel isActive={index === activeIndex}>
          {option.icon}
          {option.name}
        </TextTabLabel>
      </TextTabButton>
    );
  });

  // `tL` and `t2` pad the row with empty spacers so the first and last card
  // can still centre themselves: `tL` only when modern, `t2` always.
  const spacer = isModernVibrantDesign || (isVibrantDesign && isModernDesign);

  return (
    <Frame
      isModernDesign={isModernDesign}
      isVibrantDesign={isVibrantDesign}
      isModernSlateDesign={isModernSlateDesign}
    >
      {canScrollLeft && (
        <ScrollArrow
          direction="left"
          aria-label="Scroll categories left"
          onClick={() =>
            scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" })
          }
        >
          <ChevronLeftIcon />
        </ScrollArrow>
      )}
      <ScrollPort ref={scrollRef} isModernSlateDesign={isModernSlateDesign}>
        <TabRow isVibrantDesign={isVibrantDesign}>
          {spacer && <div />}
          {items}
          {spacer && <div />}
        </TabRow>
      </ScrollPort>
      {canScrollRight && (
        <ScrollArrow
          direction="right"
          aria-label="Scroll categories right"
          onClick={() =>
            scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" })
          }
        >
          <ChevronRightIcon />
        </ScrollArrow>
      )}
    </Frame>
  );
}

/* ------------------------- ne / t9 — the loading state --------------------- */

const SkeletonFrame = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isModernDesign",
})<{ isModernDesign?: boolean }>(({ theme, isModernDesign }) => ({
  position: "relative",
  zIndex: Z_INDEX.LOW,
  height: 50,
  marginBottom: PAGE_PADDING,
  ...(isModernDesign
    ? {}
    : {
        padding: `0px ${PAGE_PADDING}px`,
        [theme.breakpoints.up("lg")]: { padding: `0 ${CONTENT_GUTTER.lg}` },
        [theme.breakpoints.up("xl")]: { padding: `0 ${CONTENT_GUTTER.xl}` },
        [theme.breakpoints.up("xxl")]: { padding: `0 ${CONTENT_GUTTER.xxl}` },
        [theme.breakpoints.up("xxxl")]: { padding: `0 ${CONTENT_GUTTER.xxxl}` },
      }),
}));

/* ------------------------ nn / nt — the sticky section --------------------- */

/** `nn` — the sticky wrapper. Parks under whichever navbar is in play. */
const StickyWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "scrollDirection" &&
    prop !== "isVibrantDesign" &&
    prop !== "isSlateDesign",
})<{
  scrollDirection?: "up" | "down";
  isVibrantDesign?: boolean;
  isSlateDesign?: boolean;
}>(({ theme, scrollDirection, isVibrantDesign, isSlateDesign }) => {
  const parked = isSlateDesign ? SLATE_APPBAR_HEIGHT : APPBAR_HEIGHT;
  return {
    position: "sticky",
    top: scrollDirection === "up" ? parked : 0,
    zIndex: Z_INDEX.BASE,
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    transition: "top 0.6s ease-in-out, box-shadow 0.3s ease-in-out",
    // VIBRANT is the exception again: its navbar keeps auto-hiding above `md`,
    // so the strip must keep following it rather than locking to the offset.
    ...(isVibrantDesign
      ? {}
      : {
          [theme.breakpoints.up("md")]: {
            top: parked,
            transitionDuration: "unset",
          },
          [theme.breakpoints.up("xxl")]: { top: parked },
        }),
  };
});

export interface CategoryTabsProps {
  options: CategoryTabOption[];
  /** Index of the category currently under the header. */
  tabIndex: number;
  onTabChange: (index: number) => void;
  scrollDirection?: "up" | "down";
  flags: DesignFlags;
}

/** `nt` — `TabsSection`. Renders the skeleton until options arrive. */
export default function CategoryTabs({
  options,
  tabIndex,
  onTabChange,
  scrollDirection = "up",
  flags,
}: CategoryTabsProps) {
  return (
    <StickyWrapper
      className="tabs-category"
      scrollDirection={scrollDirection}
      isVibrantDesign={flags.isVibrantDesign}
      isSlateDesign={flags.isSlateDesign}
    >
      {options.length > 0 ? (
        <CategoryTabsHeader
          options={options}
          // `activeIndex: r < n.length ? r : 0` — the original's own guard
          // against an index left over from a longer catalog.
          activeIndex={tabIndex < options.length ? tabIndex : 0}
          onTabChange={onTabChange}
          flags={flags}
        />
      ) : (
        <SkeletonFrame
          direction="row"
          spacing={1}
          alignItems="center"
          isModernDesign={flags.isModernDesign}
        >
          <Skeleton variant="rounded" height={45} width="100%" />
        </SkeletonFrame>
      )}
    </StickyWrapper>
  );
}

/** Re-exported so the page can size its own scroll margins consistently. */
export { gutterFor };
